import React, { lazy, Suspense } from 'react'
import { observable } from 'mobx'
import { observer } from 'mobx-react'
import { Button, Row } from 'react-bootstrap'
import {
  getAddressFieldsJsonBasedOnCountry,
  getAddressOnlyFieldsForValidation,
  checkIfTwoFormValuesAreEqual,
} from 'src/deps'
import {
  addressContainer,
  storeContainer,
  accountsAddressContainer,
  accountsContainer,
  customerContainer,
} from 'src/models'
import { i18nTranslate } from 'src/utils'
import { AddressValidation, toastState } from 'src/views/components'
import { SavedLegalAddressDetail } from './SavedLegalAddressDetail'
import './styles.scss'

@observer
class SavedLegalAddressList extends React.Component {
  @observable currentlySelectedAddress = null
  @observable isToLoadAllAddress = false
  @observable selectedAddressIdForEdit = ''
  @observable isToDisableSavedContinueButton = false
  @observable isToShowAddressSuggestionModal = false
  @observable isEnteredAddressInvalid = false
  @observable recommendationAddresses = []

  currentMarket = storeContainer?.defaultShippingRegion
    ? 'Address' + storeContainer?.defaultShippingRegion
    : 'AddressUS'
  AddressForm = lazy(
    () =>
      import(
        'src/views/addresses/' + this.currentMarket + '/' + this.currentMarket
      )
  )

  constructor(props) {
    super(props)
  }

  componentDidMount() {
    const { savedAddressList = [] } = this.props
    const defaultAddress =
      savedAddressList?.filter(address => address?.isDefault) || []
    if (defaultAddress?.length > 0) {
      this.currentlySelectedAddress = defaultAddress[0]
    } else {
      this.currentlySelectedAddress = savedAddressList[0]
    }
  }

  getFormFieldsBasedOnCountry = async () => {
    let fieldJson = await getAddressFieldsJsonBasedOnCountry(
      storeContainer?.defaultShippingRegion
    )
    let formFields = await getAddressOnlyFieldsForValidation(
      fieldJson,
      'legalAddress'
    )
    return formFields
  }

  handleSavedAddressRadioSelect = address => {
    this.currentlySelectedAddress = address
  }

  toggleViewMoreAddressList = () => {
    this.isToLoadAllAddress = !this.isToLoadAllAddress
  }

  toggleEditAddressView = (address = {}, isToDisableContinue = false) => {
    this.selectedAddressIdForEdit = address || ''
    this.isToDisableSavedContinueButton = isToDisableContinue
  }

  handleAddressSuggestionModalClose = () => {
    this.isToShowAddressSuggestionModal = false
  }

  handleAddressSuggestionModalContinue = async formData => {
    const formDataTemp = {
      ...formData,
      addressId:
        formData?.addressId ||
        formData?.id ||
        this.selectedAddressIdForEdit?.addressId ||
        '',
      isDefault:
        formData?.isDefault ||
        this.selectedAddressIdForEdit?.isDefault ||
        false,
      setDefaultAddress:
        formData?.isDefault ||
        this.selectedAddressIdForEdit?.isDefault ||
        false,
      deliveryInstruction:
        formData?.deliveryInstruction ||
        formData?.shippingInstructions ||
        this.selectedAddressIdForEdit?.deliveryInstruction ||
        this.selectedAddressIdForEdit?.shippingInstructions ||
        '',
    }
    if (formDataTemp?.selectedIndex <= 0) {
      let validateOriginalAddressResponse =
        await addressContainer.originalAddressApiCall(formDataTemp)
      this.handleAddressSuggestionModalClose()
      if (addressContainer.isSuccessResponse(validateOriginalAddressResponse)) {
        await addressContainer.updateProfileAddress(formDataTemp)
        await this.props?.getSavedAddressList()
        this.toggleEditAddressView()
      } else {
        if (
          validateOriginalAddressResponse?.message === 'input.validation.failed'
        ) {
          toastState.setToastMessage(
            i18nTranslate(
              'legalAddress.pleaseCorrectTheAddress',
              'Please correct the address to proceed.'
            )
          )
        } else if (
          validateOriginalAddressResponse?.code ===
          'ONESOURCE_ADPLGIN_VAL_ERROR'
        ) {
          toastState.setToastMessage(
            i18nTranslate(
              'legalAddress.invalidAddress',
              'Address provided is invalid'
            )
          )
        }
      }
    } else {
      await addressContainer.updateProfileAddress(formDataTemp)
      await this.props?.getSavedAddressList()
      this.handleAddressSuggestionModalClose()
      this.toggleEditAddressView()
    }
  }

  handleUpdateSavedAddress = async (formData, savedAddress) => {
    const formDataTemp = {
      ...formData,
      isDefault: savedAddress?.isDefault || false,
      shippingInstructions:
        savedAddress?.shippingInstructions ||
        savedAddress?.deliveryInstruction ||
        '',
      deliveryInstruction:
        savedAddress?.shippingInstructions ||
        savedAddress?.deliveryInstruction ||
        '',
      addressId: savedAddress?.addressId,
    }
    const { isToMakeValidationApiCall = false } = formData
    const isToMakeAddressValidationApi =
      storeContainer?.defaultShippingRegion === 'US' ||
      storeContainer?.defaultShippingRegion === 'CA' ||
      isToMakeValidationApiCall
    let formFields = await this.getFormFieldsBasedOnCountry()
    const isSavedAddressSameAsEnteredAddress =
      await checkIfTwoFormValuesAreEqual(formFields, formDataTemp, savedAddress)
    if (isToMakeAddressValidationApi && !isSavedAddressSameAsEnteredAddress) {
      const validationResponse =
        await addressContainer.validateAddress(formDataTemp)
      if (validationResponse?.code === 'ADCOVAL0003') {
        this.recommendationAddresses = [{ ...formDataTemp }, []]
        this.isEnteredAddressInvalid = true
        this.isToShowAddressSuggestionModal = true
      } else if (validationResponse?.code == 'ONESOURCE_ADPLGIN_VAL_ERROR') {
        this.isToShowAddressSuggestionModal = false
        toastState.setToastMessage(
          i18nTranslate('legalAddress.addressNotAdded', 'Address not added')
        )
        return
      } else if (addressContainer.isSuccessResponse(validationResponse)) {
        const recommendationAddress =
          validationResponse?.address?.validationDetails?.[0]
            ?.recommendations || []
        let firstAddress = recommendationAddress?.[0] || {}
        let isFirstSuggestedAddressSameAsCurrentAddress =
          await checkIfTwoFormValuesAreEqual(
            formFields,
            formDataTemp,
            firstAddress
          )

        if (
          Object.keys(firstAddress)?.length > 0 &&
          !isFirstSuggestedAddressSameAsCurrentAddress
        ) {
          this.recommendationAddresses = [
            { ...formDataTemp },
            ...recommendationAddress,
          ]
          this.isEnteredAddressInvalid = false
          this.isToShowAddressSuggestionModal = true
        } else {
          this.handleAddressSuggestionModalContinue(formDataTemp)
        }
      } else {
        this.isToShowAddressSuggestionModal = false
        toastState.setToastMessage(
          i18nTranslate('legalAddress.addressNotAdded', 'Address not added')
        )
        return
      }
    } else {
      await this.handleAddressSuggestionModalContinue(formDataTemp)
    }
  }

  handleSavedAddressContinue = async (formData = {}) => {
    this.isToDisableSavedContinueButton = true
    const accountAddress =
      accountsContainer?.profileResponse?.addresses?.[0] || {}
    const { accountId = '', addressId = '' } = accountAddress
    const {
      firstName = '',
      lastName = '',
      email = '',
      phone = '',
      phoneNumber = '',
    } = customerContainer?.profileResponse || {}
    const accountIdFromProfile = customerContainer?.accountId || ''

    const formDataTemp = {
      ...formData,
      firstName,
      lastName,
      email,
      phone: phone || phoneNumber,
      accountId: accountId || accountIdFromProfile || '',
      addressId,
    }
    let updateAddressResponse

    if (Object.keys(accountAddress)?.length > 0) {
      updateAddressResponse =
        await accountsAddressContainer.updateAccountAddress(formDataTemp, false)
    } else {
      updateAddressResponse =
        await accountsAddressContainer.addAddress(formDataTemp)
    }

    if (accountsAddressContainer.isSuccessResponse(updateAddressResponse)) {
      await accountsContainer.getProfile(customerContainer.accountId)
      this.isToDisableSavedContinueButton = false
      this.props?.handleNextAccordionContinue?.()
    } else {
      this.isToDisableSavedContinueButton = false
      toastState.setToastMessage(
        i18nTranslate(
          'legalAddress.pleaseSelectDifferent',
          'Please try a different address.'
        )
      )
    }
  }

  renderContinueButtonForSavedAddress = () => {
    return (
      <Row noGutters className="continue-button-wrapper justify-content-end">
        <Button
          className="legal-saved-address-continue-button mt-3 px-5 py-2"
          variant="primary"
          disabled={this.isToDisableSavedContinueButton}
          type="submit"
          onClick={() =>
            this.handleSavedAddressContinue(this.currentlySelectedAddress)
          }
          data-testid="qa-legal-address-saved-continue">
          {i18nTranslate('legalAddress.addNewAddressContinue', 'Continue')}
        </Button>
      </Row>
    )
  }

  renderEditAddressSaveButton = () => {
    return (
      <Row noGutters className="edit-changes-button-wrapper mt-3">
        <Button
          variant="primary"
          className="edit-legal-address-button"
          type="submit"
          data-testid="qa-legal-address-edit-save-button">
          {i18nTranslate('legalAddress.editSaveChanges', 'Save Changes')}
        </Button>
        <Button
          variant="outline-primary"
          className="legal-address-cancel-edit-changes-button ml-3"
          onClick={() => this.toggleEditAddressView()}
          data-testid="qa-legal-address-edit-cancel-changes">
          {i18nTranslate('legalAddress.cancelEditChanges', 'Cancel')}
        </Button>
      </Row>
    )
  }

  renderEditAddressForm = address => {
    const AddressForm = this.AddressForm
    return (
      <AddressForm
        addressInfo={address}
        onSubmit={this.handleUpdateSavedAddress}
        renderSubmitButton={this.renderEditAddressSaveButton}
        type="legalAddress"
        className="legal-address-add-new-form"
      />
    )
  }

  renderEditButtonForSavedAddress = address => {
    // todo - Edit functionality TBD with BA
    return (
      <Button
        className="legal-address-edit-button d-none"
        variant="link"
        onClick={() => this.toggleEditAddressView(address, true)}
        data-testid="qa-legal-address-edit-button">
        {i18nTranslate('legalAddress.editChanges', 'Edit')}
      </Button>
    )
  }

  renderViewMoreButton = () => {
    const { savedAddressList = [] } = this.props
    return (
      <>
        {savedAddressList?.length > 2 && !this.isToLoadAllAddress && (
          <div className="legal-address-view-more-button-wrapper">
            <button
              data-testid="qa-legal-address-view-more-button"
              className="legal-address-view-more-button"
              variant="link"
              onClick={this.toggleViewMoreAddressList}>
              {i18nTranslate('legalAddress.viewMoreButton', 'View More')}
            </button>
          </div>
        )}
      </>
    )
  }

  renderSavedAddress = address => {
    return (
      <div
        className="d-flex justify-content-between"
        data-testid="qa-legal-address-saved-address-detail">
        <span>
          <SavedLegalAddressDetail address={address} />
        </span>
        <span>{this.renderEditButtonForSavedAddress(address)}</span>
      </div>
    )
  }

  renderSavedAddressRadioOption = address => {
    const { addressId = '' } = address
    return (
      <>
        <input
          type="radio"
          name="savedAddress"
          id={addressId}
          onChange={() => this.handleSavedAddressRadioSelect(address)}
          key={'radio' + addressId}
          checked={this.currentlySelectedAddress?.addressId === addressId}
          className="saved-address-radio-option"
          data-testid={`qa-legal-saved-address-${addressId}`}
        />
        <label
          htmlFor={addressId}
          className="saved-address-label-wrapper border-bottom my-2 pb-2">
          <div
            className="saved-address-label"
            data-testid={`qa-legal-address-saved-address-${addressId}-label`}>
            {this.renderSavedAddress(address)}
          </div>
        </label>
      </>
    )
  }

  renderSavedAddressView = address => {
    const { addressId = '' } = address
    return this.selectedAddressIdForEdit?.addressId == addressId
      ? this.renderEditAddressForm(address)
      : this.renderSavedAddressRadioOption(address)
  }

  render() {
    const { savedAddressList = [] } = this.props
    if (savedAddressList?.length === 0) {
      return <></>
    }

    return (
      <>
        <Suspense fallback={<div>Loading...</div>}>
          {savedAddressList
            ?.filter(address =>
              this.selectedAddressIdForEdit?.addressId
                ? this.selectedAddressIdForEdit?.addressId == address?.addressId
                : true
            )
            ?.filter((address, index) =>
              this.isToLoadAllAddress ? true : index <= 1
            )
            ?.map(address => this.renderSavedAddressView(address))}
          {this.renderViewMoreButton()}
          {this.renderContinueButtonForSavedAddress()}
        </Suspense>
        {this.isToShowAddressSuggestionModal && (
          <AddressValidation
            open={this.isToShowAddressSuggestionModal}
            addressInvalidFlag={this.isEnteredAddressInvalid}
            recommendations={this.recommendationAddresses}
            handleClose={this.handleAddressSuggestionModalClose}
            handleClickOnPro={this.handleAddressSuggestionModalContinue}
          />
        )}
      </>
    )
  }
}

export default SavedLegalAddressList
export { SavedLegalAddressList }
